
import { supabase } from "@/integrations/supabase/client";
import { ReferralFriend, ReferralReward } from "./types";

export const fetchReferralCode = async (userId: string) => {
  const { data, error } = await supabase
    .from('referral_codes')
    .select('*')
    .eq('user_id', userId)
    .single();
    
  if (error && error.code !== 'PGRST116') {
    console.error("Error fetching referral code:", error);
    throw error;
  }
  
  return data;
};

export const fetchReferredFriends = async (userId: string) => {
  // Fetch referred relationships
  const { data: relationshipsData, error: relationshipsError } = await supabase
    .from('referral_relationships')
    .select(`
      id,
      referred_id,
      paid_status,
      paid_date,
      created_at
    `)
    .eq('referrer_id', userId);
    
  if (relationshipsError) {
    console.error("Error fetching referral relationships:", relationshipsError);
    throw relationshipsError;
  }
  
  if (!relationshipsData || relationshipsData.length === 0) {
    return [];
  }
  
  // Get user details for each referred user
  const referredUserIds = relationshipsData.map(rel => rel.referred_id);
  
  const { data: userRolesData, error: userRolesError } = await supabase
    .from('user_roles')
    .select('user_id, first_name, last_name')
    .in('user_id', referredUserIds);
    
  if (userRolesError) {
    console.error("Error fetching user roles:", userRolesError);
    throw userRolesError;
  }
  
  // Map the data to the ReferralFriend format
  const friends: ReferralFriend[] = relationshipsData.map(rel => {
    const userRole = userRolesData?.find(u => u.user_id === rel.referred_id);
    // We might not have the email since we can only get the current user's email
    const email = "invited-user@metrade.app"; // Generic placeholder
    
    return {
      id: rel.referred_id,
      email: email,
      firstName: userRole?.first_name || null,
      lastName: userRole?.last_name || null,
      isPaid: rel.paid_status || false,
      joinedAt: rel.created_at
    };
  });
  
  return friends;
};

export const fetchReferralRewards = async (userId: string) => {
  const { data, error } = await supabase
    .from('referral_rewards')
    .select('*')
    .eq('user_id', userId);
    
  if (error) {
    console.error("Error fetching referral rewards:", error);
    throw error;
  }
  
  const rewards: ReferralReward[] = (data || []).map(reward => ({
    id: reward.id,
    milestoneCount: reward.milestone_count,
    rewardType: reward.reward_type,
    rewardValue: reward.reward_value,
    claimed: reward.claimed,
    claimedDate: reward.claimed_date
  }));
  
  return rewards;
};

export const updateRewardClaimStatus = async (rewardId: string) => {
  const { error } = await supabase
    .from('referral_rewards')
    .update({ 
      claimed: true, 
      claimed_date: new Date().toISOString() 
    })
    .eq('id', rewardId);
    
  if (error) {
    console.error("Error claiming reward:", error);
    throw error;
  }
  
  return true;
};

export const processReferralRewards = async (userId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("No active session");
    }
    
    const response = await supabase.functions.invoke('process-referral-reward', {
      body: { userId },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (response.error) {
      console.error("Error processing referral rewards:", response.error);
      throw response.error;
    }
    
    return response.data;
  } catch (error) {
    console.error("Error in processReferralRewards:", error);
    throw error;
  }
};

// Add the missing function that combines data needed by useReferrals
export const fetchReferralData = async (userId: string) => {
  try {
    // Get referral code
    const referralCodeData = await fetchReferralCode(userId);
    
    // Get referred friends
    const referredFriends = await fetchReferredFriends(userId);
    
    // Calculate referral stats
    const totalReferrals = referredFriends.length;
    const paidReferrals = referredFriends.filter(friend => friend.isPaid).length;
    
    // Calculate next milestone (e.g., increments of 5)
    const milestoneIncrement = 5;
    const nextMilestone = Math.ceil((totalReferrals + 1) / milestoneIncrement) * milestoneIncrement;
    
    return {
      referralCode: referralCodeData?.code || null,
      referredFriends,
      totalReferrals,
      paidReferrals,
      nextMilestone
    };
  } catch (error) {
    console.error("Error in fetchReferralData:", error);
    throw error;
  }
};
